import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button } from '../../components/ui/Kit';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import BootCampTab from './BootCampTab';
import AgentResourcesTab from './AgentResourcesTab';
import { useAuth } from '../../contexts/AuthContext';

const {
  FiTool, FiFileText, FiDollarSign, FiSend, FiBook,
  FiExternalLink, FiArrowRight, FiInbox,
  FiClipboard, FiShield, FiTrendingUp, FiChevronDown, FiChevronUp, FiVideo,
} = FiIcons;

const TABS = [
  { id: 'boot-camp', label: 'Boot Camp', icon: FiVideo },
  { id: 'agent-resources', label: 'Agent Resources', icon: FiClipboard },
  { id: 'scripts', label: 'Scripts', icon: FiFileText },
  { id: 'calculators', label: 'Calculators', icon: FiDollarSign },
  { id: 'quote-tools', label: 'Quote Tools', icon: FiDollarSign },
  { id: 'apply', label: 'Apply', icon: FiSend },
  { id: 'resources', label: 'Resources', icon: FiBook },
];

const QUOTE_TOOLS = [
  {
    carrier: 'Corebridge',
    tool: 'QoL Rapid Rater',
    url: 'https://rapid-rater.live.web.corebridgefinancial.com/QoLRapidRater',
  },
  {
    carrier: 'Corebridge',
    tool: 'Simplinow Quoter',
    url: 'https://rapid-rater.live.web.corebridgefinancial.com/Simplinowquoter',
  },
  {
    carrier: 'Mutual of Omaha',
    tool: 'Mobile Quotes',
    url: 'https://www3.mutualofomaha.com/mobile-quotes/#/',
  },
  {
    carrier: 'North American',
    tool: 'Free Term Quote',
    url: 'https://www.northamericancompany.com/free-term-quote?utm_source=na_agent&utm_medium=site&utm_campaign=addvantageterm_launch&utm_term=product_page&utm_content=buttonlink#/termQuote',
  },
];

const APPLY_LINKS = [
  {
    carrier: 'Corebridge',
    tool: 'Connext Portal',
    url: 'https://www.connext.corebridgefinancial.com/life/connext-portal/public/login',
  },
  {
    carrier: 'Mutual of Omaha',
    tool: 'Agent SSO Login',
    url: 'https://accounts.mutualofomaha.com/samlAuthnRequest',
  },
  {
    carrier: 'Mutual of Omaha',
    tool: 'Direct Applications',
    url: 'https://www3.mutualofomaha.com/OktaSpaRegistration/home',
  },
  {
    carrier: 'Ethos',
    tool: 'Agents Portal',
    url: 'https://agents.ethoslife.com/login',
  },
  {
    carrier: 'North American',
    tool: 'Agent Portal',
    url: 'https://www.northamericancompany.com/?logout=true',
  },
  {
    carrier: 'Foresters',
    tool: 'Agent Portal',
    url: 'https://www.foresters.com/',
  },
];

function LinkCard({ carrier, tool, url }) {
  return (
    <Card className="flex flex-col border-l-4 border-l-primary">
      <div className="p-5 flex flex-col flex-1">
        <p className="text-base font-bold text-gray-900">{carrier}</p>
        <p className="text-sm text-gray-500 mt-0.5 mb-4">{tool}</p>
        <div className="mt-auto">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            Open Tool
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
          </a>
        </div>
      </div>
    </Card>
  );
}

function ScriptsTab() {
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center py-12">
      <Card className="max-w-md w-full text-center p-8">
        <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FiFileText} className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Call Workspace</h2>
        <p className="text-sm text-gray-500 mb-6">
          Access your interactive call scripts, dialer, objection handler, and SMS templates in one place.
        </p>
        <Button onClick={() => navigate('/workspace')} size="lg" className="w-full">
          Open Call Workspace
          <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
        </Button>
      </Card>
    </div>
  );
}

function CardGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((item) => (
        <LinkCard key={`${item.carrier}-${item.tool}`} {...item} />
      ))}
    </div>
  );
}

function ResourcesTab() {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <SafeIcon icon={FiInbox} className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">No resources yet</h3>
      <p className="text-sm text-gray-500 max-w-xs">
        Your admin will add carrier guides, training materials, and links here soon.
      </p>
    </div>
  );
}

const PAYOUT_PERIODS = [5, 10, 15, 20, 25, 30];
const PAYOUT_FREQUENCIES = ['Monthly', 'Quarterly', 'Annually'];

function formatCurrency(value) {
  if (value == null || isNaN(value)) return '—';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function AnnuityEstimator() {
  const [lumpSum, setLumpSum] = useState('');
  const [annualRate, setAnnualRate] = useState('');
  const [years, setYears] = useState('');
  const [frequency, setFrequency] = useState('Monthly');

  const results = useMemo(() => {
    const pv = parseFloat(lumpSum);
    const ratePercent = parseFloat(annualRate);
    const n = parseFloat(years);
    if (!pv || pv <= 0 || !ratePercent || ratePercent <= 0 || !n || n <= 0) return null;

    let periodsPerYear;
    if (frequency === 'Monthly') periodsPerYear = 12;
    else if (frequency === 'Quarterly') periodsPerYear = 4;
    else periodsPerYear = 1;

    const r = ratePercent / 100 / periodsPerYear;
    const totalPeriods = n * periodsPerYear;
    const payment = pv * (r * Math.pow(1 + r, totalPeriods)) / (Math.pow(1 + r, totalPeriods) - 1);
    const totalPayout = payment * totalPeriods;
    const totalInterest = totalPayout - pv;

    return { payment, totalPayout, totalInterest };
  }, [lumpSum, annualRate, years, frequency]);

  const frequencyLabel = frequency === 'Monthly' ? 'Monthly' : frequency === 'Quarterly' ? 'Quarterly' : 'Annual';

  return (
    <div className="mt-4 border-t border-gray-200 pt-5 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Lump Sum ($)</label>
          <input
            type="number"
            placeholder="e.g. 100000"
            value={lumpSum}
            onChange={(e) => setLumpSum(e.target.value)}
            onWheel={(e) => e.target.blur()}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Annual Interest Rate (%)</label>
          <input
            type="number"
            placeholder="e.g. 4.5"
            value={annualRate}
            onChange={(e) => setAnnualRate(e.target.value)}
            onWheel={(e) => e.target.blur()}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Payout Period (Years)</label>
          <select
            value={years}
            onChange={(e) => setYears(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] outline-none"
          >
            <option value="">Select period</option>
            {PAYOUT_PERIODS.map((p) => (
              <option key={p} value={p}>{p} years</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Payout Frequency</label>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-[#00AAFF] focus:ring-1 focus:ring-[#00AAFF] outline-none"
          >
            {PAYOUT_FREQUENCIES.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-2">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">{frequencyLabel} Payment</p>
          <p className="text-2xl font-bold text-[#00AAFF]">
            {results ? formatCurrency(results.payment) : '—'}
          </p>
        </div>
        <div className="flex gap-6 pt-1">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Total Payout</p>
            <p className="text-sm font-medium text-gray-900">
              {results ? formatCurrency(results.totalPayout) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Total Interest Earned</p>
            <p className="text-sm font-medium text-gray-900">
              {results ? formatCurrency(results.totalInterest) : '—'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CalculatorsTab() {
  const navigate = useNavigate();
  const [estimatorOpen, setEstimatorOpen] = useState(false);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Card 1 — Financial Needs Analysis */}
      <Card className="flex flex-col border-l-4 border-l-[#00AAFF]">
        <div className="p-5 flex flex-col flex-1">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
            <SafeIcon icon={FiClipboard} className="w-5 h-5 text-[#00AAFF]" />
          </div>
          <p className="text-base font-bold text-gray-900">Financial Needs Analysis (FNA)</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Walk your client through a full needs analysis using the Prosperity Leaders FNA tool.
          </p>
          <div className="mt-auto">
            <a
              href="https://app.prosperityleaders.net/fin/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Open FNA Tool
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </a>
          </div>
        </div>
      </Card>

      {/* Card 2 — Coverage Calculator */}
      <Card className="flex flex-col border-l-4 border-l-[#00AAFF]">
        <div className="p-5 flex flex-col flex-1">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
            <SafeIcon icon={FiShield} className="w-5 h-5 text-[#00AAFF]" />
          </div>
          <p className="text-base font-bold text-gray-900">Coverage Calculator</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Calculate your client's total insurable need based on debt, income replacement, and education goals.
          </p>
          <div className="mt-auto">
            <button
              onClick={() => navigate('/workspace')}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Open Calculator
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* Card 3 — Annuity Estimator */}
      <Card className="flex flex-col border-l-4 border-l-[#00AAFF]">
        <div className="p-5 flex flex-col flex-1">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-[#00AAFF]" />
          </div>
          <p className="text-base font-bold text-gray-900">Annuity Estimator</p>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Estimate monthly annuity payouts based on lump sum, interest rate, and payout period.
          </p>
          <div className="mt-auto">
            <button
              onClick={() => setEstimatorOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Calculate
              <SafeIcon icon={estimatorOpen ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
            </button>
          </div>
          {estimatorOpen && <AnnuityEstimator />}
        </div>
      </Card>
    </div>
  );
}

export default function Toolkit() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('quote-tools');
  const hasSetDefaultTab = useRef(false);

  useEffect(() => {
    if (hasSetDefaultTab.current || !user) return;
    hasSetDefaultTab.current = true;
    if (user.role === 'setter' || user.role === 'associate') {
      setActiveTab('boot-camp');
    }
  }, [user]);

  return (
    <div className="w-full max-w-full space-y-6 px-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gray-100 rounded-lg">
          <SafeIcon icon={FiTool} className="h-6 w-6 text-gray-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Toolkit</h1>
          <p className="text-sm text-gray-500">Scripts, quoting tools, applications, and resources</p>
        </div>
      </div>

      <div className="border-b border-gray-200 bg-white rounded-t-lg">
        <nav className="flex gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div>
        {activeTab === 'boot-camp' && <BootCampTab />}
        {activeTab === 'agent-resources' && <AgentResourcesTab />}
        {activeTab === 'scripts' && <ScriptsTab />}
        {activeTab === 'calculators' && <CalculatorsTab />}
        {activeTab === 'quote-tools' && <CardGrid items={QUOTE_TOOLS} />}
        {activeTab === 'apply' && <CardGrid items={APPLY_LINKS} />}
        {activeTab === 'resources' && <ResourcesTab />}
      </div>
    </div>
  );
}
